import { useState } from "react"
import { FormProvider, useForm, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { toast } from "@/hooks/useToast"
import {
  AddressInput,
  Ch_betterDocRecommendationsInput,
} from "@/modules/patients/detail/better-doc/ch/services/__generated__/betterDocRecommendationsQuery.graphql"
import { fetchBetterDocRecommendations } from "@/modules/patients/detail/better-doc/ch/services/betterDocRecommendationsQuery"
import { BetterDocConsent } from "@/modules/patients/detail/better-doc/components/better-doc-consent"
import { BetterDocConsentAdditionalInformation } from "@/modules/patients/detail/better-doc/components/better-doc-consent-additional-information"
import { MedicalInformation } from "@/modules/patients/detail/better-doc/components/better-doc-medical-information"
import type { BetterDocSpecialty } from "@/modules/patients/detail/better-doc/lib/better-doc-sub-specialties"
import {
  BetterDocSpecialistSearchFormSchema,
  betterDocSpecialistSearchSchema,
} from "@/modules/patients/detail/better-doc/schema/better-doc-specialist-search-schema"
import { useBetterDocSuggestionsStoreContext } from "@/modules/patients/detail/better-doc/store/useBetterDocSuggestionsStore"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { useParentEpisodeOfCareContext } from "@/modules/patients/detail/progress-note/point-of-care/point-of-care-context"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientBodyMeasurementsOutputFragment$key } from "@/modules/patients/services/__generated__/PatientBodyMeasurementsOutputFragment.graphql"
import { PatientConditionInformationOutputFragment$key } from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientBodyMeasurementsOutputFragment } from "@/modules/patients/services/PatientBodyMeasurementsOutputFragment"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import type { PointOfCareType } from "@/types/__generated__/union-types"

type ChBetterDocSearchDialogProps = {
  onClose: () => void
  specialtyName?: BetterDocSpecialty
  pointOfCareSpecialtyId?: string
  isBetterDocSpecialtyFixed?: boolean
  effectivePointOfCare?: PointOfCareType
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  address: AddressInput | null
  phoneNumber: string | null
  betterDocInInsurance: { insureeNumber: string; productKey: string; organizationName: string } | null
}

export const ChBetterDocSearchDialog = ({
  onClose,
  specialtyName,
  pointOfCareSpecialtyId,
  isBetterDocSpecialtyFixed = false,
  effectivePointOfCare,
  episodeOfCareProgressNoteData,
  address,
  phoneNumber,
  betterDocInInsurance,
}: ChBetterDocSearchDialogProps) => {
  const { t, i18n } = useTranslation()
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()
  const [isLoading, setIsLoading] = useState(false)

  const { activeMedications } = useMedications()
  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const parentEpisodeOfCare = useParentEpisodeOfCareContext()
  const { setValue, trigger } = useFormContext<PointOfCareFormType>()
  const setBetterDocSuggestions = useBetterDocSuggestionsStoreContext((store) => store.setBetterDocSuggestions)

  if (!patientData.patientById) {
    throw new Error("Patient not found")
  }

  const { conditionInformation, bodyMeasurements } = patientData.patientById
  const { language } = episodeOfCareProgressNoteData.appointment

  const patientBodyMeasurements = useFragment<PatientBodyMeasurementsOutputFragment$key>(
    PatientBodyMeasurementsOutputFragment,
    bodyMeasurements?.edges,
  )
  const conditionInformationData = useFragment<PatientConditionInformationOutputFragment$key>(
    PatientConditionInformationOutputFragment,
    conditionInformation,
  )

  const form = useForm<BetterDocSpecialistSearchFormSchema>({
    resolver: zodResolver(betterDocSpecialistSearchSchema),
    defaultValues: {},
  })
  const { handleSubmit, getValues } = form

  const handleFormSubmit = handleSubmit(async () => {
    setIsLoading(true)
    const currentMedications = activeMedications.map((medication) => medication.name)
    const bodyMassIndex = patientBodyMeasurements?.at(0)?.node.bodyMassIndex || null
    const healthInformationConditionIcd10Codes =
      conditionInformationData?.conditions.map((conditions) => conditions.icdDiagnosis.diagnosisCode) || []

    if (!address) {
      toast({ description: t("episodeOfCare.betterDoc.error.addressNotFound") })
      setIsLoading(false)
      return
    }

    if (!phoneNumber) {
      toast({ description: t("episodeOfCare.betterDoc.error.phoneNumberNotFound") })
      setIsLoading(false)
      return
    }

    if (specialtyName && betterDocInInsurance && effectivePointOfCare) {
      const formValues = getValues()
      const betterDocRecommendationsInput: Ch_betterDocRecommendationsInput = {
        progressNoteId: episodeOfCareProgressNoteData.id,
        address,
        currentMedications,
        bodyMassIndex,
        insurance: betterDocInInsurance,
        healthInformationConditionIcd10Codes,
        phoneNumber,
        patientId,
        language,
        consultationReason: formValues.reasonForConsultations,
        effectivePointOfCare,
        specialtyId: isBetterDocSpecialtyFixed ? null : (pointOfCareSpecialtyId ?? null),
        subSpecialtyName: formValues.subSpecialty,
        progressNoteAnamnesis: episodeOfCareProgressNoteData.anamnesis,
        episodeOfCareIcd10Code: parentEpisodeOfCare.icdDiagnosis.diagnosisCode,
      }

      const betterDocRecommendationsData = await fetchBetterDocRecommendations(
        betterDocRecommendationsInput,
        i18n.language,
      )

      if (betterDocRecommendationsData?.status === "MANUAL_SEARCH_NEEDED") {
        setValue("isBetterDocSuggestion", true, { shouldDirty: true })
        setValue("betterDocSuggestionStatus", "MANUAL_SEARCH_NEEDED", { shouldDirty: true })
        trigger("careProviderId")
      } else {
        const careProviders = betterDocRecommendationsData?.careProviders
        setBetterDocSuggestions(episodeOfCareProgressNoteData.id, careProviders)
        if (careProviders && careProviders.length > 0) {
          setValue("isBetterDocSuggestion", true, { shouldDirty: true, shouldValidate: true })
          setValue("careProviderId", careProviders[0].id, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      }
      handleCloseDialog()
      FaroInsightsWrapper.pushEvent("better_doc_consent_send")
    }
  })

  const handleCloseDialog = () => {
    setIsLoading(false)
    onClose()
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="better-doc-dialog-content"
        className="flex h-[calc(100vh-120px)] flex-col">
        <DialogHeader>
          <DialogTitle data-testid="better-doc-dialog-title">
            {t("episodeOfCare.betterDoc.searchDialog.title")}
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <div className="mx-6 my-4 flex justify-between">
            <div className="flex max-w-142 grow flex-col items-start gap-8" data-testid="consent">
              <BetterDocConsent />
              {specialtyName && (
                <MedicalInformation
                  specialtyName={specialtyName}
                  isBetterDocSpecialtyFixed={isBetterDocSpecialtyFixed}
                />
              )}
            </div>
            <BetterDocConsentAdditionalInformation />
          </div>
          <DialogFooter className="mt-auto flex p-6 pt-4" data-testid="better-doc-dialog-footer">
            <Button className="px-4 py-2" variant="secondary" onClick={handleCloseDialog} data-testid="cancel-button">
              {t("common.button.cancel")}
            </Button>
            <Button
              isLoading={isLoading}
              className="px-4 py-2"
              variant="primary"
              data-testid="send-request-button"
              onClick={handleFormSubmit}>
              {t("common.button.sendRequest")}
            </Button>
          </DialogFooter>
        </FormProvider>
      </DialogContentCustom>
    </Dialog>
  )
}
